import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../../../components/Navigation/Sidebar';
import Topbar from '../../../components/Navigation/Topbar';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { connect } from "react-redux";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Sector,
    Cell,
} from 'recharts';
import { useAutomaticAssignmentSocket } from '../../../hooks/automaticAssignmentSocket';

const getPreviousMonthRange = () => {
    const now = new Date();
    const prevMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
    const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    
    const pad = (num) => num.toString().padStart(2, '0');
    const from = `${year}-${pad(prevMonth + 1)}-01`;
    const lastDay = new Date(year, prevMonth + 1, 0).getDate();
    const to = `${year}-${pad(prevMonth + 1)}-${pad(lastDay)}`;
    
    return { from, to };
};

const getPercentageToProgressBar = (currentStatusCount, eachCountStatus) => {
    if (!eachCountStatus) 
        return 0;

    const {assignedStatus, pendingStatus, failedStatus} = eachCountStatus

    const totalCount = parseInt(assignedStatus) + parseInt(pendingStatus) + parseInt(failedStatus);

    if (totalCount === 0) {
        return 0;
    }

    const finalPercent = Math.round((parseInt(currentStatusCount)/totalCount)*100);

    return finalPercent;
};

const customStyles = {
    AssignedOrdersColorCard: {
        background: '#57C785',
        background: 'linear-gradient(90deg,rgba(87, 199, 133, 0.76) 58%, rgba(87, 199, 133, 1) 100%)',
    },
    PendingOrdersColorCard: {
        background: '#FFC34D',
        background: 'linear-gradient(90deg,rgba(255, 195, 77, 0.76) 58%, rgba(255, 195, 77, 1) 100%)',
    },
    FailedOrdersColorCard: {
        background: '#e74a3b',
        background: 'linear-gradient(90deg, rgba(231, 74, 59, 0.76) 58%, rgba(231, 74, 59, 1) 100%)',    
    }
};

const COLORS = ["#82ca9d", "#a0d8b3", "#c2e9d0", "#ffc658", "#8dd1e1", "#d0ed57", "#ffb6b9", "#b39ddb"];

const CustomTooltip = ({ active, payload, label, addressesAssignedByMonth }) => {
    if (active && payload && payload.length && addressesAssignedByMonth && label) {
        const weekNumber = label.replace('Semana ', '');
        const weekData = addressesAssignedByMonth.items.find(item => item.semana === Number(weekNumber));
        const { fecha_inicio, fecha_fin } = weekData || {};

        return (
            <div className="custom-tooltip" style={{ background: "#fff", border: "1px solid #ccc", padding: 10 }}>
                <strong className="label">{label}</strong>
                <p>{new Date(fecha_inicio).toISOString().slice(0, 10)} / {new Date(fecha_fin).toISOString().slice(0, 10)}</p>
                {payload.map((entry, idx) => (
                    <div key={idx} style={{ color: entry.color }}>
                        {entry.name}: {entry.value}
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const renderActiveShape = (props) => {
    const RADIAN = Math.PI / 180;
    const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';

    return (
        <g>
            <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill}>
                {payload.name}
            </text>
            <Sector
                cx={cx}
                cy={cy}
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
            />
            <Sector
                cx={cx}
                cy={cy}
                startAngle={startAngle}
                endAngle={endAngle}
                innerRadius={outerRadius + 6}
                outerRadius={outerRadius + 10}
                fill={fill}
            />
            <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
            <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
            <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill="#333">{`${value} órdenes`}</text>
            <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} dy={18} textAnchor={textAnchor} fill="#999">
                {`(${(percent * 100).toFixed(2)}%)`}
            </text>
        </g>
    );
};

const formatBarChartList = (addressesAssignedByMonth) => {
    if (!addressesAssignedByMonth || !Array.isArray(addressesAssignedByMonth.items)) {
        return [];
    }
    return addressesAssignedByMonth.items.map((item) => ({
        name: `Semana ${item.semana}`,
        Asignadas: item.total_asignados,
        Reasignadas: item.total_reasignados,
    }));
}

const formatPieChartList = (addressesPreviousWeekPieChart) => {
    if (!addressesPreviousWeekPieChart || !Array.isArray(addressesPreviousWeekPieChart.items)) {
        return [];
    }

    return addressesPreviousWeekPieChart.items.map((item) => ({
        name: item.company,
        value: Number(item.total_records)
    }));
}

function HomeAssigner({
    addressesAssignedByMonth,
    countAddressesToAutomaticAssingment,
    addressesPreviousWeekPieChart,
    detailProgressBarToAutomaticAssingment,
    currentAddressThatHasBeenChangedSocket,
    reduxGetCountAssignedAdressesByMonth,
    reduxGetAllCountAddressesToautomaticAssigment,
    reduxGetAllCountAddressesToautomaticAssigmentSocket,
    reduxGetAddressesPreviousWeekPieChart,
    reduxGetDetailToProgressBarToAutomaticAssigment,
    reduxResetAddressesPreviousMonth,
}) {
    const [activeIndex, setActiveIndex] = useState(0);

    const { subscribe } = useAutomaticAssignmentSocket();

    useEffect(() => {
        const { from, to } = getPreviousMonthRange();

        reduxGetCountAssignedAdressesByMonth({ to, from });
        reduxGetAddressesPreviousWeekPieChart();
        reduxGetDetailToProgressBarToAutomaticAssigment();

        return () => {
            reduxResetAddressesPreviousMonth();
        };
    }, []);
    
    useEffect(() => {
        reduxGetAllCountAddressesToautomaticAssigment();
    }, [reduxGetAllCountAddressesToautomaticAssigment]);

    useEffect(() => {
        reduxGetAllCountAddressesToautomaticAssigmentSocket();
        reduxGetDetailToProgressBarToAutomaticAssigment();
    }, [currentAddressThatHasBeenChangedSocket]);

    const onPieEnter = (_, index) => {
        setActiveIndex(index);
    };

    return (
        <div id='wrapper'>
            <Sidebar />
            <div id='content-wrapper' className='d-flex flex-column'>
                <div id='content'>
                    <Topbar />
                    <div className='container-fluid'>
                        <div className="d-sm-flex align-items-center mb-1">
                            <h1 className="h3 mb-0 text-gray-800 mr-3">Asignaciones Automáticas</h1>
                        </div>
                        <p className="mb-4">Órdenes asignadas automáticamente</p>

                        <div className='row'>
                            <div className='col-xl-4 col-md-4 mb-4'>
                                <Link to='/assigner/assigned-orders' className='text-decoration-none'>
                                    <div className='card shadow h-100 py-2' style={customStyles.AssignedOrdersColorCard}>
                                        <div className='card-body'>
                                            <div className='row no-gutters align-items-center'>
                                                <div className='col mr-2'>
                                                    <div className='text-xs font-weight-bold text-uppercase mb-1 text-white'>
                                                        Ordenes Asignadas hoy
                                                    </div>
                                                    <div className='h5 mb-0 font-weight-bold text-white'>
                                                        {countAddressesToAutomaticAssingment?.assignedStatus}
                                                    </div>
                                                </div>
                                                <div className='col-auto'>
                                                    <i className='fas fa-clipboard-list fa-2x text-white'></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </div>

                            <div className='col-xl-4 col-md-4 mb-4'>
                                <Link to='/assigner/pending-orders' className='text-decoration-none'>
                                    <div className='card shadow h-100 py-2' style={customStyles.PendingOrdersColorCard}>
                                        <div className='card-body'>
                                            <div className='row no-gutters align-items-center'>
                                                <div className='col mr-2'>
                                                    <div className='text-xs font-weight-bold text-uppercase mb-1 text-white'>
                                                        Ordenes Pendientes hoy
                                                    </div>
                                                    <div className='h5 mb-0 font-weight-bold text-white'>
                                                        {countAddressesToAutomaticAssingment?.createdStatus}
                                                    </div>
                                                </div>
                                                <div className='col-auto'>
                                                    <i className='fas fa-hourglass-half fa-2x text-white'></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </div>

                            <div className='col-xl-4 col-md-4 mb-4'>
                                <Link to='/assigner/failed-orders' className='text-decoration-none'>
                                    <div className='card shadow h-100 py-2' style={customStyles.FailedOrdersColorCard}>
                                        <div className='card-body'>
                                            <div className='row no-gutters align-items-center'>
                                                <div className='col mr-2'>
                                                    <div className='text-xs font-weight-bold text-uppercase mb-1 text-white'>
                                                        Ordenes Fallidas Hoy
                                                    </div>
                                                    <div className='h5 mb-0 font-weight-bold text-white'>
                                                        {countAddressesToAutomaticAssingment?.failedStatus}
                                                    </div>
                                                </div>
                                                <div className='col-auto'>
                                                    <i className='fas fa-file-excel fa-2x text-white'></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        </div>

                        <div style={{ position: 'relative', marginBottom: '24px' }}>
                            <div style={{
                                position: 'absolute',
                                top: '-20px',
                                width: '100%',
                                textAlign: 'center',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                zIndex: 1,
                            }}>
                                {`${getPercentageToProgressBar(detailProgressBarToAutomaticAssingment?.assignedStatus, detailProgressBarToAutomaticAssingment)}% asignado | ${getPercentageToProgressBar(detailProgressBarToAutomaticAssingment?.failedStatus, detailProgressBarToAutomaticAssingment)}% fallido`}
                            </div>
                            <ProgressBar>
                                <ProgressBar 
                                    key={1}
                                    now={getPercentageToProgressBar(detailProgressBarToAutomaticAssingment?.assignedStatus, detailProgressBarToAutomaticAssingment)} 
                                    variant="" 
                                    striped 
                                    animated
                                    className="home-automatic-assignment-progress-bar"
                                />
                                <ProgressBar 
                                    key={2}
                                    now={getPercentageToProgressBar(detailProgressBarToAutomaticAssingment?.failedStatus, detailProgressBarToAutomaticAssingment)} 
                                    variant="danger" 
                                    striped 
                                    animated
                                />
                            </ProgressBar>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <div style={{ width: '48%', height: 300, textAlign: 'center' }}>
                                <h5>Órdenes asignadas el mes anterior</h5>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        width={500}
                                        height={300}
                                        data={formatBarChartList(addressesAssignedByMonth)}
                                        margin={{
                                            top: 5,
                                            right: 30,
                                            left: 20,
                                            bottom: 5,
                                        }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip content={props => <CustomTooltip {...props} addressesAssignedByMonth={addressesAssignedByMonth} />} />
                                        <Legend />
                                        <Bar dataKey="Asignadas" fill="#82ca9d" />
                                        <Bar dataKey="Reasignadas" fill="#8884d8" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <div style={{ width: '50%', height: 300, textAlign: 'center' }}>
                                <h5>Órdenes asignadas la semana anterior</h5>
                                <div>
                                    {
                                        formatPieChartList(addressesPreviousWeekPieChart)[activeIndex]
                                            ? `Cliente: ${formatPieChartList(addressesPreviousWeekPieChart)[activeIndex].name}`
                                            : ''
                                    }
                                </div>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart width={400} height={400}>
                                        <Pie
                                            activeIndex={activeIndex}
                                            activeShape={renderActiveShape}
                                            data={formatPieChartList(addressesPreviousWeekPieChart)}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={80}
                                            fill="#8884d8"
                                            dataKey="value"
                                            onMouseEnter={onPieEnter}
                                        >
                                            {formatPieChartList(addressesPreviousWeekPieChart).map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const mapStateToProps = (state) => ({
    addressesAssignedByMonth: state.addressToAutomaticAssigmentState.addressesAssignedByMonth,
    countAddressesToAutomaticAssingment: state.addressToAutomaticAssigmentState.countAddressesToAutomaticAssingment,
    addressesPreviousWeekPieChart: state.addressToAutomaticAssigmentState.addressesPreviousWeekPieChart,
    detailProgressBarToAutomaticAssingment: state.addressToAutomaticAssigmentState.detailProgressBarToAutomaticAssingment,
    currentAddressThatHasBeenChangedSocket: state.addressToAutomaticAssigmentState.currentAddressThatHasBeenChangedSocket,
});

const mapDispatchToProps = (dispatch) => ({
    reduxGetCountAssignedAdressesByMonth: (payload) =>
        dispatch({
            type: "GetCountAssignedAdressesByMonth",
            value: payload
        }),
    reduxGetAllCountAddressesToautomaticAssigment: (payload) =>
        dispatch({
            type: "GetCountAllAddressesToAutomaticAssigment"
        }),
    reduxGetAllCountAddressesToautomaticAssigmentSocket: (payload) =>
        dispatch({
            type: "GetCountAllAddressesToAutomaticAssigmentSocket"
        }),
    reduxGetAddressesPreviousWeekPieChart: (payload) =>
        dispatch({
            type: "GetAddressesPreviousWeekPieChart"
        }),
    reduxGetDetailToProgressBarToAutomaticAssigment: () =>
        dispatch({
            type: "GetDetailToProgressBarToAutomaticAssigment"
        }),
    reduxResetAddressesPreviousMonth: (payload) =>
        dispatch({
            type: "COUNT_ASSIGNED_ADDRESSES_BY_MONTH_RESET"
        }),
});

export default connect(mapStateToProps, mapDispatchToProps)(HomeAssigner);